<template lang="pug">
b-list-group-item(class="d-flex align-items-center notif-item")
    b-icon(:icon="isOn ? 'bell-fill' : 'bell-slash'" class="mr-3 notif-icon" :class="{ 'notif-icon-on': isOn }")
    div(class="flex-fill")
        div Notifications
        small(class="notif-status" :class="statusClass") {{ statusText }}
    div(class="ml-auto d-flex align-items-center")
        b-spinner(v-if="busy" small class="mr-2 text-muted")
        b-form-checkbox(
            switch
            size="lg"
            v-model="switchValue"
            @change="onToggle"
            :disabled="busy || !supported || blocked"
        )
</template>

<script>
/* eslint-disable */
export default {
    name: 'NotificationToggle',
    data() {
        return {
            supported: true,
            optedIn: false,
            permission: 'default', // 'default' | 'granted' | 'denied'
            busy: false,
            switchValue: false,
        };
    },
    computed: {
        isOn() {
            return this.supported && this.permission === 'granted' && this.optedIn;
        },
        blocked() {
            return this.permission === 'denied';
        },
        statusText() {
            if (!this.supported) return 'Not supported on this device';
            if (this.blocked) return 'Blocked — enable in device settings';
            return this.isOn ? 'On' : 'Off';
        },
        statusClass() {
            if (!this.supported || this.blocked) return 'text-danger';
            return this.isOn ? 'text-success' : 'text-muted';
        },
    },
    async mounted() {
        await this.initStatus();
    },
    beforeDestroy() {
        try {
            this.$OneSignal.Notifications.removeEventListener('permissionChange', this.syncFromSdk);
            this.$OneSignal.User.PushSubscription.removeEventListener('change', this.syncFromSdk);
        } catch (e) { /* listeners may not have been attached */ }
    },
    methods: {
        async initStatus() {
            try {
                if (typeof this.$OneSignal?.Notifications?.isPushSupported === 'function') {
                    this.supported = this.$OneSignal.Notifications.isPushSupported();
                }
                if (!this.supported) return;
                this.syncFromSdk();
                // Keep the toggle in sync if permission/subscription changes elsewhere.
                this.$OneSignal.Notifications.addEventListener('permissionChange', this.syncFromSdk);
                this.$OneSignal.User.PushSubscription.addEventListener('change', this.syncFromSdk);
            } catch (e) {
                console.error('Notification init error', e);
                this.supported = false;
            }
        },
        syncFromSdk() {
            try {
                this.permission = this.$OneSignal.Notifications.permissionNative || 'default';
                this.optedIn = !!this.$OneSignal.User.PushSubscription.optedIn;
                // Keep the switch aligned with the real state (reverts optimistic flips).
                this.switchValue = this.isOn;
                localStorage.setItem('notificationEnabled', String(this.isOn));
            } catch (e) {
                console.error('Notification sync error', e);
            }
        },
        async onToggle(desired) {
            if (this.busy) return;
            this.busy = true;
            try {
                if (desired) {
                    await this.turnOn();
                } else {
                    await this.turnOff();
                }
            } catch (e) {
                console.error('Notification toggle error', e);
                this.toast('Could not update notifications. Please try again.', 'danger');
            } finally {
                this.syncFromSdk();
                this.busy = false;
            }
        },
        async turnOn() {
            let perm = this.$OneSignal.Notifications.permissionNative || 'default';
            if (perm === 'denied') {
                this.toast('Notifications are blocked. Please enable them in your browser or device settings.', 'warning');
                return;
            }
            if (perm !== 'granted') {
                await this.$OneSignal.Notifications.requestPermission();
                perm = this.$OneSignal.Notifications.permissionNative || 'default';
            }
            if (perm === 'granted') {
                // Identify the user (phone) first, then opt the subscription in, so the
                // opt-in is recorded against the correct user after a previous logout.
                await this.loginOneSignal();
                await this.$OneSignal.User.PushSubscription.optIn();
                this.toast('Notifications turned on.', 'success');
            } else {
                this.toast('Notification permission was not granted.', 'warning');
            }
        },
        async turnOff() {
            // Unsubscribe this device's push subscription...
            await this.$OneSignal.User.PushSubscription.optOut();
            // ...and detach the caregiver's external id (phone) so notifications
            // targeted by external id no longer reach this device.
            try {
                await this.$OneSignal.logout();
            } catch (e) {
                console.error('OneSignal logout error', e);
            }
            this.toast('Notifications turned off.', 'secondary');
        },
        async loginOneSignal() {
            try {
                const phone = this.$store.state.currentUser?.[0]?.crb5c_contactnumbermobile;
                if (!phone) {
                    console.error('No phone number to link OneSignal');
                    return;
                }
                await this.$OneSignal.login(`${phone}`);
            } catch (e) {
                console.error('OneSignal login error', e);
            }
        },
        toast(message, variant) {
            this.$bvToast.toast(message, { title: 'Notifications', variant, solid: true });
        },
    },
};
</script>

<style lang="scss" scoped>
.notif-item {
    cursor: default;
}
.notif-icon {
    color: #6c757d;
}
.notif-icon-on {
    color: #28a745;
}
.notif-status {
    display: block;
    line-height: 1.1;
}
</style>
